import { useState } from 'react';
import { useAuthContext } from '../../hooks/auth';
import { useClearAppCachesOnMount } from '../../hooks/useClearAppCachesOnMount';
import { Helmet } from 'react-helmet-async';
import { tabulateTickets } from '../../functions/tabulateTickets';
import { TextAreaErrors } from '../../components/ui/textArea';
import Editor from '@monaco-editor/react';
import { InputsInfosPages } from '../../components/ui/inputs';
import { ButtonTabulation } from '../../components/ui/buttons';
import Modal from '../../components/ui/modal';
import { displayError, displayWarning } from '../../utils/messageToast';

export default function Tabulation() {
  const { authorization, authorizationForce, user, authorizationRA } = useAuthContext();
  const ambient = user?.ambient as string;
  const isInForce = user?.inForce === true;
  const token = isInForce ? authorizationForce : authorization;
  const [loadingButton, setLoadingButton] = useState(false);
  const [tabId, setTabId] = useState('');
  const [protocols, setProtocols] = useState<any[]>([]);
  const [textAreaValue, setTextAreaValue] = useState<string>('');
  const [publicNote, setPublicNote] = useState<string>('');
  const [privateNote, setPrivateNote] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState<any[]>([]);
  const [successes, setSuccesses] = useState<any[]>([]);

  useClearAppCachesOnMount();

  const handleTextAreaValue = async (value: string) => {
    const lines = value.split('\n').map(line => ({ protocol: line }));
    setProtocols(lines);
  }

  const handleTabulation = async () => {
    try {
      setLoadingButton(true);
      await tabulateTickets(
        tabId,
        setTabId,
        ambient,
        token,
        authorizationRA,
        setTextAreaValue,
        publicNote,
        setPublicNote,
        privateNote,
        setPrivateNote,
        protocols,
        setProtocols,
        setErrors,
        setSuccesses,
        user?.google?.id,
        user?.google?.name ?? user?.userName ?? null,
        user?.clientId ?? null,
        user?.clientName ?? null,
        isInForce
      );
      setLoadingButton(false);
      setIsModalOpen(false);
    } catch (error: any) {
      displayError("Erro ao tabular tickets!");
      setLoadingButton(false);
    }
  };

  return (
    <div className="bg-page">
      <Helmet>
        <title>Tabulação em massa</title>
      </Helmet>

      <div className="bg-page-tabulation w-full px-2 sm:px-4 md:px-6 py-4 payload-page">
        <div className="form-page-shell">
          <div className="form-config-card">
            <div className="form-card-header">
              <div>
                <h2 className="form-card-title">Tabulação em massa</h2>
                <p className="form-card-subtitle">Defina a tabulação e notas padrão</p>
              </div>
            </div>

            <div className="form-card-body">
              <div className="infos-form-header w-full space-y-3 sm:space-y-4">
                <div className="form-info-row">
                  <label className="form-info-label">TabId</label>
                  <div className="header-fields-item flex-1 w-full">
                    <InputsInfosPages
                      placeholder='Insira a tabId de destino'
                      type='text'
                      value={tabId}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setTabId(event.target.value)
                      }}
                    />
                  </div>
                </div>

                <div className="form-info-row">
                  <label className="form-info-label">Nota Pública</label>
                  <div className="header-fields-item flex-1 w-full">
                    <InputsInfosPages
                      placeholder='Insira a nota pública'
                      type='text'
                      value={publicNote}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setPublicNote(event.target.value)
                      }}
                    />
                  </div>
                </div>

                <div className="form-info-row">
                  <label className="form-info-label">Nota Privada</label>
                  <div className="header-fields-item flex-1 w-full">
                    <InputsInfosPages
                      placeholder='Insira a nota privada'
                      type='text'
                      value={privateNote}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setPrivateNote(event.target.value)
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-payload-actions">
                <ButtonTabulation
                  onClick={() => {
                    if (tabId === '') {
                      displayWarning("Insira a tabId de destino!");
                      return;
                    }
                    if (textAreaValue === '') {
                      displayWarning("Insira os protocolos!");
                      return;
                    }
                    // if (publicNote === '') {
                    //   displayWarning("Insira a nota pública!");
                    //   return;
                    // }
                    // if (privateNote === '') {
                    //   displayWarning("Insira a nota privada!");
                    //   return;
                    // }
                    handleTabulation();
                    setIsModalOpen(true)
                  }}
                />
              </div>
            </div>
          </div>

          <div className="form-payload-card">
            <div className="form-payload-header">
              <h3 className="form-card-title">Protocolos</h3>
              <p className="form-card-subtitle">Um protocolo por linha</p>
            </div>
            <div className="form-payload-body">
              <div className="form-payload-editor">
                <Editor
                  height="360px"
                  language="text"
                  value={textAreaValue}
                  onChange={(value) => {
                    const next = value ?? '';
                    setTextAreaValue(next);
                    handleTextAreaValue(next);
                  }}
                  theme="vs-light"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    tabSize: 2,
                    automaticLayout: true,
                  }}
                />
              </div>
            </div>
          </div>

          {successes.length > 0 && (
            <div className="form-payload-card">
              <div className="form-payload-header">
                <h3 className="form-card-title">Tabulados com sucesso</h3>
                <p className="form-card-subtitle">Protocolos processados corretamente</p>
              </div>
              <div className="form-payload-body">
                <TextAreaErrors
                  value={JSON.stringify(successes, null, 2)}
                  readOnly
                />
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div className="form-payload-card">
              <div className="form-payload-header">
                <h3 className="form-card-title">Erros</h3>
                <p className="form-card-subtitle">Itens que falharam na tabulação</p>
              </div>
              <div className="form-payload-body">
                <TextAreaErrors
                  value={JSON.stringify(errors, null, 2)}
                  readOnly
                />
              </div>
            </div>
          )}

          {isModalOpen && (
            <Modal
              isOpen={isModalOpen}
              loading={loadingButton}
              onConfirm={handleTabulation}
              onClose={() => setIsModalOpen(false)}
            >
              <p className="mb-5 text-base sm:text-lg">Confirma a tabulação dos tickets?</p>
            </Modal>
          )}

        </div>
      </div>
    </div>
  );
}
